export interface ShaderPreset {
  id: string;
  name: string;
  description: string;
  // Shadertoy-compatible GLSL fragment shader
  fs: string;
}

// Each shader accepts uPrimaryColor and uSecondaryColor uniforms (vec3, 0–1 range)
// so the animation tints to the tournament's brand colors.

const hexGridShader = `
uniform vec3 uPrimaryColor;
uniform vec3 uSecondaryColor;

float hexDist(vec2 p) {
  p = abs(p);
  float c = dot(p, normalize(vec2(1.0, 1.732)));
  return max(c, p.x);
}

vec4 hexCoords(vec2 uv) {
  vec2 r = vec2(1.0, 1.732);
  vec2 h = r * 0.5;
  vec2 a = mod(uv, r) - h;
  vec2 b = mod(uv - h, r) - h;
  vec2 gv = dot(a, a) < dot(b, b) ? a : b;
  float x = atan(gv.x, gv.y);
  float y = 0.5 - hexDist(gv);
  vec2 id = uv - gv;
  return vec4(x, y, id.x, id.y);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  uv.x *= iResolution.x / iResolution.y;
  uv *= 8.0;

  vec4 hc = hexCoords(uv);
  float dist = hc.y;
  float edge = smoothstep(0.0, 0.08, dist);

  float hash = fract(sin(dot(hc.zw, vec2(127.1, 311.7))) * 43758.5);
  float pulse = sin(iTime * 0.6 + hash * 6.283) * 0.5 + 0.5;
  pulse *= pulse;

  vec3 hexColor = mix(uPrimaryColor * 0.15, uSecondaryColor * 0.4, pulse);
  vec3 edgeColor = mix(uPrimaryColor * 0.6, uSecondaryColor * 0.9, pulse);
  vec3 col = mix(edgeColor, hexColor, edge);

  // Light that travels around the perimeter of each hex
  const float PI = 3.14159265;
  float edgeMask = smoothstep(0.12, 0.0, dist);
  float travelPos = mod(iTime * 1.2 + hash * 6.283, 2.0 * PI) - PI;
  float angDiff = abs(hc.x - travelPos);
  angDiff = min(angDiff, 2.0 * PI - angDiff);
  float head = smoothstep(0.7, 0.0, angDiff);
  float tail = smoothstep(1.8, 0.0, angDiff) * 0.4;
  float trace = (head + tail) * edgeMask;
  col += trace * uSecondaryColor * 1.8;
  col += head * edgeMask * uPrimaryColor * 0.8;

  // Diagonal sweep of light across the whole grid
  float sweepCoord = (hc.z + hc.w) * 0.08;
  float sweep = smoothstep(0.06, 0.0, abs(fract(sweepCoord - iTime * 0.02) - 0.5) - 0.46);
  col += sweep * uSecondaryColor * 0.5;

  col *= 0.85;

  fragColor = vec4(col, 1.0);
}
`;

const plasmaShader = `
uniform vec3 uPrimaryColor;
uniform vec3 uSecondaryColor;

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float t = iTime * 0.6;

  float v1 = sin(uv.x * 10.0 + t);
  float v2 = sin(uv.y * 10.0 + t * 1.3);
  float v3 = sin((uv.x + uv.y) * 8.0 + t * 0.9);
  float cx = uv.x - 0.5 + sin(t * 0.4) * 0.2;
  float cy = uv.y - 0.5 + cos(t * 0.5) * 0.2;
  float v4 = sin(sqrt(cx * cx + cy * cy) * 15.0 - t * 1.8);
  float v5 = sin(uv.x * 18.0 + uv.y * 6.0 + t * 2.2);
  float plasma = (v1 + v2 + v3 + v4 + v5) * 0.2;

  float blend = plasma * 0.5 + 0.5;
  vec3 col = mix(uPrimaryColor * 0.5, uSecondaryColor * 0.7, blend);

  // Bright traveling band that adds clear sense of motion
  float band = sin((uv.x + uv.y) * 6.0 - iTime * 2.0);
  float highlight = smoothstep(0.85, 1.0, band);
  col += highlight * uSecondaryColor * 0.35;

  col *= 0.65;

  fragColor = vec4(col, 1.0);
}
`;

const starsShader = `
uniform vec3 uPrimaryColor;
uniform vec3 uSecondaryColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  vec3 col = vec3(0.02, 0.02, 0.06);

  for (float i = 0.0; i < 4.0; i++) {
    vec2 grid = uv * (80.0 + i * 40.0);
    vec2 id = floor(grid);
    vec2 gv = fract(grid) - 0.5;
    float h = hash(id + i * 100.0);
    float twinkle = sin(iTime * (0.3 + h * 0.6) + h * 6.283) * 0.5 + 0.5;
    float dist = length(gv);
    float star = smoothstep(0.05, 0.0, dist) * twinkle;
    vec3 starColor = mix(uPrimaryColor, uSecondaryColor, h);
    col += star * starColor * (0.3 + 0.7 * (1.0 - i / 4.0));
  }

  // shooting star
  float trail = mod(iTime * 0.08 + uv.x * 0.5 - uv.y * 2.0, 3.0);
  float beam = smoothstep(0.005, 0.0, abs(uv.y - (0.3 + sin(iTime * 0.08) * 0.1))) * smoothstep(1.0, 0.0, trail) * 0.4;
  col += beam * uPrimaryColor;

  fragColor = vec4(col, 1.0);
}
`;

const neonLinesShader = `
uniform vec3 uPrimaryColor;
uniform vec3 uSecondaryColor;

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  vec3 col = vec3(0.03, 0.03, 0.05);

  float t = iTime * 0.12;

  for (float i = 0.0; i < 8.0; i++) {
    float speed = 0.6 + i * 0.15;
    float angle = 0.5 + i * 0.3;
    float pos = mod(t * speed + i * 0.125, 1.0);
    vec2 dir = normalize(vec2(cos(angle), sin(angle)));
    float d = abs(dot(uv - pos * vec2(1.0, 1.0), dir));
    float line = 0.002 / (d + 0.001);
    vec3 lineColor = i < 4.0 ? uPrimaryColor : uSecondaryColor;
    float brightness = sin(iTime * 0.5 + i * 1.1) * 0.3 + 0.5;
    col += line * lineColor * brightness * 0.4;
  }

  fragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`;

const circuitShader = `
uniform vec3 uPrimaryColor;
uniform vec3 uSecondaryColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.0, 289.0))) * 45758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  uv.x *= iResolution.x / iResolution.y;

  vec3 col = vec3(0.02, 0.04, 0.03);
  float scale = 12.0;
  vec2 grid = uv * scale;
  vec2 id = floor(grid);
  vec2 gv = fract(grid);

  float h = hash(id);
  float isH = step(0.5, fract(h * 5.0));

  float pulse = mod(iTime * 0.5 + h * 3.0, 4.0);
  float activeCell = smoothstep(1.5, 0.0, abs(pulse - 2.0));

  float lineWidth = 0.04;
  float lineH = smoothstep(lineWidth, 0.0, abs(gv.y - 0.5)) * isH;
  float lineV = smoothstep(lineWidth, 0.0, abs(gv.x - 0.5)) * (1.0 - isH);
  float node = smoothstep(0.08, 0.0, length(gv - 0.5));

  // Direction of travel per cell: +1 or -1 from hash
  float dir = h > 0.5 ? 1.0 : -1.0;
  // Tracing pulse position along this cell's line, 0..1 cycling
  float tracePos = fract(iTime * 0.8 + h * 1.7);
  // Coordinate along the line: horizontal line uses gv.x, vertical uses gv.y
  float lineCoord = mix(gv.y, gv.x, isH);
  float traceHead = fract(tracePos * dir * 0.5 + 0.5);
  float traceDist = abs(lineCoord - traceHead);
  // Light head along the wire
  float trace = smoothstep(0.15, 0.0, traceDist);
  trace *= (lineH + lineV);

  // Comet tail trailing behind the head
  float tailCoord = lineCoord - traceHead;
  float tail = smoothstep(0.25, 0.0, -tailCoord * dir) * smoothstep(0.0, 0.02, tailCoord * -dir);
  tail *= (lineH + lineV) * 0.6;

  vec3 lineColor = mix(uPrimaryColor * 0.3, uSecondaryColor * 0.7, activeCell);
  col += (lineH + lineV + node) * lineColor;
  col += (trace + tail) * uSecondaryColor * 1.4;
  col += node * activeCell * uPrimaryColor * 0.8;

  fragColor = vec4(col, 1.0);
}
`;

const fireShader = `
uniform vec3 uPrimaryColor;
uniform vec3 uSecondaryColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float t = iTime * 0.5;

  float n = noise(uv * 4.0 + vec2(0.0, -t)) * 0.5;
  n += noise(uv * 8.0 + vec2(0.0, -t * 1.5)) * 0.25;
  n += noise(uv * 16.0 + vec2(0.0, -t * 2.0)) * 0.125;

  float fire = n - uv.y * 0.7;
  fire = clamp(fire * 2.0, 0.0, 1.0);

  vec3 col = mix(vec3(0.0), uPrimaryColor * 1.2, fire);
  col = mix(col, uSecondaryColor * 1.5, fire * fire);
  col *= 0.7;

  fragColor = vec4(col, 1.0);
}
`;

const auroraBorealisShader = `
uniform vec3 uPrimaryColor;
uniform vec3 uSecondaryColor;

float hash(float n) { return fract(sin(n) * 43758.5453); }

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float n = i.x + i.y * 57.0;
  return mix(mix(hash(n), hash(n + 1.0), f.x),
             mix(hash(n + 57.0), hash(n + 58.0), f.x), f.y);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float t = iTime * 0.15;

  vec3 col = vec3(0.01, 0.02, 0.05);

  for (float i = 0.0; i < 5.0; i++) {
    float speed = 0.3 + i * 0.1;
    float wave = sin(uv.x * (3.0 + i) + t * speed + i) * 0.15 + 0.4 + i * 0.1;
    float band = smoothstep(0.06, 0.0, abs(uv.y - wave));
    float n = noise(vec2(uv.x * 5.0 + t, i * 3.3));
    band *= n * 0.8 + 0.2;
    vec3 bandColor = i < 2.5 ? uPrimaryColor : uSecondaryColor;
    bandColor *= 0.6;
    col += band * bandColor * (0.4 + 0.6 * (i / 5.0));
  }

  fragColor = vec4(col, 1.0);
}
`;

export const SHADER_PRESETS: ShaderPreset[] = [
  {
    id: 'hex_grid',
    name: 'Hex Grid',
    description: 'Pulsing hexagonal grid',
    fs: hexGridShader,
  },
  {
    id: 'plasma',
    name: 'Plasma',
    description: 'Flowing plasma waves',
    fs: plasmaShader,
  },
  {
    id: 'stars',
    name: 'Starfield',
    description: 'Drifting stars with shooting star',
    fs: starsShader,
  },
  {
    id: 'neon_lines',
    name: 'Neon Lines',
    description: 'Dark background with neon scan lines',
    fs: neonLinesShader,
  },
  {
    id: 'circuit',
    name: 'Circuit Board',
    description: 'Animated circuit trace pattern',
    fs: circuitShader,
  },
  {
    id: 'fire',
    name: 'Ember Fire',
    description: 'Upward ember glow',
    fs: fireShader,
  },
  {
    id: 'aurora',
    name: 'Aurora',
    description: 'Northern lights waves',
    fs: auroraBorealisShader,
  },
];

export function getShaderPreset(id: string): ShaderPreset | undefined {
  return SHADER_PRESETS.find(p => p.id === id);
}

export function hexToVec3(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  return [r, g, b];
}
